import { Button } from 'components/ui/button';
import { Label } from 'components/ui/label';
import { Input } from 'components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';
import { Switch } from 'components/ui/switch';
import { ChipsInput, ChipsInputField, ChipsInputList } from 'components/ui/chips-input';
import { useStepper } from 'components/stepper/stepper-provider';
import { useCreateApiContext } from '../create-api-context';
import { useState, useEffect, useMemo, Fragment, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from 'hooks/use-toast';
import { JsonCodeEditor } from '../json-code-editor';
import {
  Plus,
  Trash2,
  ChevronDown,
  ChevronRight,
  PlusCircle,
} from 'lucide-react';
import { ParameterFormField } from './utils/tree-utils';
import { SYSTEM_VARIABLE_OPTIONS } from './utils/system-variable-utils';
import { useParameterTree } from './hooks/use-parameter-tree';
import { buildParameters, reconstructTree } from './utils/schema-utils';
import { PARAMETER_NAME_PLACEHOLDER, validateParameterName } from './utils/naming-utils';
import {
  INPUT_PARAMETER_TABLE_COL_SPAN,
  INPUT_PARAMETER_TABLE_GRID,
} from './utils/parameter-table-layout';
import { CreateApiParameterFormHeader } from './create-api-parameter-form-header';
import { CreateApiParameterMobileList } from './create-api-parameter-mobile-list';

const nestedObjectLevel = 3;

function getJsonObjectError(
  value: string,
  label: string,
  translate: (key: string, options?: { defaultValue?: string; label?: string }) => string
) {
  try {
    const parsed = JSON.parse(value || '{}');
    if (!parsed || Array.isArray(parsed) || typeof parsed !== 'object') {
      return translate('JSON_MUST_BE_OBJECT', {
        label,
        defaultValue: '{{label}} must be a JSON object.',
      });
    }

    return '';
  } catch (error) {
    return error instanceof Error
      ? error.message
      : translate('PLEASE_PROVIDE_VALID_JSON', {
          label: label.toLowerCase(),
          defaultValue: 'Please provide valid {{label}}.',
        });
  }
}

export function CreateApiInputParametersForm() {
  const { t } = useTranslation();
  const { nextStep } = useStepper();
  const { formData, updateFormData } = useCreateApiContext();
  const { toast } = useToast();
  const [viewMode, setViewMode] = useState<'table' | 'json'>('table');
  const [inputText, setInputText] = useState('{}');
  const [parameterErrors, setParameterErrors] = useState<Record<string, string>>({});

  const {
    parameters,
    setParameters,
    handleAddParameter,
    handleAddChildParameter,
    handleRemoveParameter,
    handleParameterChange,
    toggleExpand,
  } = useParameterTree([
    {
      id: '1',
      name: '',
      description: '',
      type: 'string',
      location: 'body',
      defaultValue: '',
      required: false,
    },
  ]);

  const namingErrors = useMemo(() => {
    const errors: Record<string, string> = {};
    const validate = (params: ParameterFormField[], parentType = '') => {
      params.forEach((p) => {
        if (parentType !== 'array') {
          const err = validateParameterName(p.name, p.location);
          if (err) errors[p.id] = err.message;
        }
        if (p.children) validate(p.children, p.type);
      });
    };
    validate(parameters);
    return errors;
  }, [parameters]);

  const hasNamingErrors = Object.keys(namingErrors).length > 0;

  const parametersRef = useRef(parameters);
  const inputTextRef = useRef(inputText);
  const viewModeRef = useRef(viewMode);
  parametersRef.current = parameters;
  inputTextRef.current = inputText;
  viewModeRef.current = viewMode;

  useEffect(() => {
    return () => {
      try {
        if (viewModeRef.current === 'json') {
          const parsed = JSON.parse(inputTextRef.current || '{}');
          if (
            parsed &&
            typeof parsed === 'object' &&
            !Array.isArray(parsed) &&
            Object.keys(parsed).length > 0
          ) {
            updateFormData({ input_parameters: parsed });
          }
          return;
        }

        const built = buildParameters(parametersRef.current, false);
        if (Object.keys(built).length > 0) {
          updateFormData({ input_parameters: built });
        }
      } catch {
        // Ignore invalid JSON while navigating away.
      }
    };
  }, [updateFormData]);

  useEffect(() => {
    if (formData.input_parameters && Object.keys(formData.input_parameters).length > 0) {
      const loadedParams = reconstructTree(formData.input_parameters);

      if (loadedParams.length > 0) {
        setParameters(loadedParams);
      }
      setInputText(JSON.stringify(formData.input_parameters, null, 2));
    }
  }, [formData.input_parameters, setParameters]);

  const inputParametersLabel = t('INPUT_PARAMETERS') || 'Input parameters';

  const validationError = useMemo(
    () => (viewMode === 'json' ? getJsonObjectError(inputText, inputParametersLabel, t) : ''),
    [inputParametersLabel, inputText, t, viewMode]
  );

  const handleSubmit = () => {
    if (viewMode === 'table') {
      const validParams = parameters.filter((p) => p.name && p.name.trim() !== '');
      const uniqueNames = new Set(validParams.map((p) => p.name));

      if (uniqueNames.size !== validParams.length) {
        toast({
          variant: 'destructive',
          title: t('DUPLICATE_PARAMETERS') || 'Duplicate parameters',
          description: t('PARAMETER_NAMES_MUST_BE_UNIQUE') || 'Parameter names must be unique',
        });
        return;
      }

      setParameterErrors({});
      const inputParameters = buildParameters(parameters, false);
      updateFormData({ input_parameters: inputParameters });
      nextStep();
    } else {
      try {
        const parsed = JSON.parse(inputText || '{}');
        if (!parsed || Array.isArray(parsed) || typeof parsed !== 'object') {
          throw new Error(
            t('INPUT_PARAMETERS_MUST_BE_JSON_OBJECT') || 'Input parameters must be a JSON object.'
          );
        }
        updateFormData({ input_parameters: parsed });
        nextStep();
      } catch (error) {
        toast({
          variant: 'destructive',
          title: t('INVALID_JSON') || 'Invalid JSON',
          description:
            error instanceof Error
              ? error.message
              : t('VALID_JSON_OBJECT_REQUIRED') || 'Please provide a valid JSON object.',
        });
      }
    }
  };

  const switchToJsonMode = () => {
    const inputParameters = buildParameters(parameters, false);
    setInputText(JSON.stringify(inputParameters, null, 2));
    setViewMode('json');
  };

  const switchToTableMode = () => {
    try {
      const parsed = JSON.parse(inputText || '{}');
      if (parsed && !Array.isArray(parsed) && typeof parsed === 'object') {
        const loadedParams = reconstructTree(parsed);
        if (loadedParams.length > 0) {
          setParameters(loadedParams);
        }
      }
      setViewMode('table');
    } catch (error) {
      toast({
        variant: 'destructive',
        title: t('INVALID_JSON') || 'Invalid JSON',
        description:
          t('CANNOT_SWITCH_TO_TABLE_MODE') || 'Cannot switch to table mode. Please fix JSON errors first.',
      });
    }
  };

  const renderRows = (
    params: ParameterFormField[],
    level = 0,
    objectDepth = 0,
    parentType = '',
  ) => {
    return params.map((param, index) => {
      const isArrayItem = parentType === 'array';

      const hasNestedChildren = param.type === 'array' || param.type === 'object';

      return (
        <Fragment key={param.id}>
          {level === 0 && index > 0 ? (
            <div className={`${INPUT_PARAMETER_TABLE_COL_SPAN} border-t border-border`} />
          ) : null}
          <div className="contents">
            <div
              className="flex items-start gap-2"
              style={{ paddingLeft: level > 0 ? `${level * 40}px` : undefined }}
            >
              <span className="inline-flex h-8 w-8 shrink-0 items-start justify-center">
                {hasNestedChildren ? (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 shrink-0 p-0 mt-0.5"
                    onClick={() => toggleExpand(param.id)}
                  >
                    {param.isExpanded ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                  </Button>
                ) : null}
              </span>
              <div className="min-w-0 flex-1 space-y-1">
                <Input
                  placeholder={
                    isArrayItem
                      ? t('ARRAY_ITEM') || 'Array Item'
                      : t('PARAMETER_NAME_PLACEHOLDER') || PARAMETER_NAME_PLACEHOLDER
                  }
                  value={isArrayItem ? '' : param.name}
                  disabled={isArrayItem}
                  onChange={(e) => handleParameterChange(param.id, 'name', e.target.value)}
                  className="h-8"
                />
                {/* {parameterErrors[param.id] && (
                  <p className="text-xs text-destructive">{parameterErrors[param.id]}</p>
                )}
                {namingErrors[param.id] && (
                  <p className="text-xs text-destructive">{namingErrors[param.id]}</p>
                )}
                {!isArrayItem && param.name.length >= 100 && (
                  <p className="text-[12px] text-destructive">
                    {t('MAX_100_CHARACTERS') || 'Max 100 characters'}
                  </p>
                )} */}
              </div>
            </div>
            <div className="space-y-1">
              <Input
                placeholder={t('ENTER_DESCRIPTION') || 'Enter description'}
                value={param.description}
                onChange={(e) => handleParameterChange(param.id, 'description', e.target.value)}
                className="h-8"
              />
              {/* {(param.description || '').length >= 100 && (
                <p className="text-[12px] text-destructive">
                  {t('MAX_100_CHARACTERS') || 'Max 100 characters'}
                </p>
              )} */}
            </div>
            <div>
              <Select
                value={param.type}
                onValueChange={(value) => handleParameterChange(param.id, 'type', value)}
              >
                <SelectTrigger className="h-8">
                  <SelectValue placeholder={t('SELECT') || 'Select'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="string">{t('STRING') || 'String'}</SelectItem>
                  <SelectItem value="number">{t('NUMBER') || 'Number'}</SelectItem>
                  <SelectItem value="integer">{t('INTEGER') || 'Integer'}</SelectItem>
                  <SelectItem value="boolean">{t('BOOLEAN') || 'Boolean'}</SelectItem>
                  {level < 10 && !isArrayItem && (
                    <SelectItem value="array">{t('ARRAY') || 'Array'}</SelectItem>
                  )}
                  {objectDepth < nestedObjectLevel && (
                    <SelectItem value="object">{t('OBJECT') || 'Object'}</SelectItem>
                  )}

                  {!isArrayItem && (
                    <>
                      <SelectSeparator />
                      <SelectItem value="variable">
                        {t('SYSTEM_VARIABLE') || 'System Variable'}
                      </SelectItem>
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Select
                value={param.location}
                onValueChange={(value) => handleParameterChange(param.id, 'location', value)}
                disabled={level > 0}
              >
                <SelectTrigger className={`h-8 ${level > 0 ? 'opacity-50' : ''}`}>
                  <SelectValue placeholder={t('SELECT') || 'Select'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="query">{t('QUERY') || 'Query'}</SelectItem>
                  <SelectItem value="body">{t('BODY') || 'Body'}</SelectItem>
                  <SelectItem value="header">{t('HEADER') || 'Header'}</SelectItem>
                  <SelectItem value="path">{t('PATH') || 'Path'}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              {isArrayItem ? (
                param.type !== 'object' && (
                  <div className="w-full">
                    <ChipsInput
                      value={(() => {
                        try {
                          const parsed = param.defaultValue ? JSON.parse(param.defaultValue) : [];
                          return Array.isArray(parsed) ? parsed : [];
                        } catch {
                          return [];
                        }
                      })()}
                      onChange={(val) =>
                        handleParameterChange(param.id, 'defaultValue', JSON.stringify(val))
                      }
                      className="max-h-[100px] min-h-[32px] overflow-y-auto"
                    >
                      <ChipsInputList />
                      <ChipsInputField />
                    </ChipsInput>
                  </div>
                )
              ) : param.type === 'variable' ? (
                <Select
                  value={param.defaultValue || SYSTEM_VARIABLE_OPTIONS[0].value}
                  onValueChange={(value) => handleParameterChange(param.id, 'defaultValue', value)}
                >
                  <SelectTrigger className="h-8">
                    <SelectValue placeholder={t('SELECT_VARIABLE') || 'Select variable'} />
                  </SelectTrigger>
                  <SelectContent>
                    {SYSTEM_VARIABLE_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                param.type !== 'array' &&
                param.type !== 'object' && (
                  <Input
                    placeholder={t('ENTER_DEFAULT_VALUE') || 'Enter default value'}
                    value={param.defaultValue}
                    onChange={(e) =>
                      handleParameterChange(param.id, 'defaultValue', e.target.value)
                    }
                    className="h-8"
                  />
                )
              )}
            </div>
            <div className="flex items-center">
              <Switch
                checked={param.required}
                onCheckedChange={(checked) => handleParameterChange(param.id, 'required', checked)}
              />
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => handleRemoveParameter(param.id)}
                disabled={isArrayItem}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
              {param.type === 'object' && level < 10 && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleAddChildParameter(param.id, param.location)}
                  className="text-primary"
                >
                  <PlusCircle className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>

          {(param.type === 'object' || param.type === 'array') &&
            param.isExpanded &&
            param.children &&
            renderRows(
              param.children,
              level + 1,
              param.type === 'object' ? objectDepth + 1 : objectDepth,
              param.type,
            )}
        </Fragment>
      );
    });
  };

  return (
    <div className="w-full min-w-0">
      <CreateApiParameterFormHeader
        title={t('INPUT_PARAMETERS') || 'Input Parameters'}
        description={
          t('CREATE_API_INPUT_PARAMETERS_DESCRIPTION') ||
          'Configure the parameters your API will receive from requests.'
        }
        viewMode={viewMode}
        onTableView={switchToTableMode}
        onJsonView={switchToJsonMode}
      />

      {viewMode === 'table' ? (
        <>
          <div className="w-full min-w-0 space-y-4">
            <CreateApiParameterMobileList
              variant="input"
              parameters={parameters}
              parameterErrors={parameterErrors}
              namingErrors={namingErrors}
              onToggleExpand={toggleExpand}
              onParameterChange={handleParameterChange}
              onRemoveParameter={handleRemoveParameter}
              onAddChildParameter={handleAddChildParameter}
            />

            <div className="hidden w-full min-w-0 overflow-x-auto rounded-lg bg-card/50 p-4 md:block">
              <div
                className={`grid ${INPUT_PARAMETER_TABLE_GRID} items-start gap-x-4 gap-y-3 min-w-max`}
              >
                <div className="text-sm font-medium text-muted-foreground">{t('NAME') || 'Name'}</div>
                <div className="text-sm font-medium text-muted-foreground">
                  {t('DESCRIPTION') || 'Description'}
                </div>
                <div className="text-sm font-medium text-muted-foreground">{t('TYPE') || 'Type'}</div>
                <div className="text-sm font-medium text-muted-foreground">
                  {t('LOCATION') || 'Location'}
                </div>
                <div className="text-sm font-medium text-muted-foreground">
                  {t('DEFAULT_VALUE') || 'Default Value'}
                </div>
                <div className="text-sm font-medium text-muted-foreground">
                  {t('REQUIRED') || 'Required'}
                </div>
                <div />
                <div className={`${INPUT_PARAMETER_TABLE_COL_SPAN} border-b border-border pb-3`} />
                {renderRows(parameters)}
              </div>
            </div>

            <div>
              <Button variant="ghost" onClick={handleAddParameter} className="text-primary">
                <Plus className="mr-2 h-4 w-4" />
                {t('ADD_PARAMETER') || 'Add Parameter'}
              </Button>
            </div>
          </div>
        </>
      ) : (
        <div className="space-y-6">
          <div className="space-y-3">
            <Label htmlFor="input-parameters">
              {t('INPUT_PARAMETERS_JSON') || 'Input Parameters JSON'}
            </Label>
            <JsonCodeEditor
              id="input-parameters"
              value={inputText}
              onChange={setInputText}
              placeholder='{"customerId":{"Type":"string","Required":true}}'
              hasError={Boolean(validationError)}
            />
            {validationError ? (
              <p className="text-sm text-destructive">{validationError}</p>
            ) : (
              <p className="text-sm text-muted-foreground">
                {t('INPUT_PARAMETERS_JSON_HINT') ||
                  'Use a JSON object keyed by parameter name. Validation happens as you type.'}
              </p>
            )}
          </div>
        </div>
      )}

      <div className="mt-6 flex items-center justify-start gap-3">
        <Button
          onClick={handleSubmit}
          disabled={viewMode === 'table' ? hasNamingErrors : Boolean(validationError)}
          className="w-full px-8 sm:w-auto"
        >
          {t('CONTINUE') || 'Continue'}
        </Button>
      </div>
    </div>
  );
}
